import React, { useState, useEffect, useRef } from 'react';
import {
    View, Text, TouchableOpacity, StyleSheet,
    ActivityIndicator, ScrollView, Animated,
} from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useAuthStore } from '../store/useAuthStore';
import { useStageTokens } from '../hooks/useStageTokens';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { AudioPromptButton } from './AudioPromptButton';
import { FriendlyButton } from './FriendlyButton';
import { ChildFriendlyBackground } from './ChildFriendlyBackground';
import { getStudentSafeActivityTitle } from '../utils/studentTaskTitle';

interface TaskQueueProps {
    studentName: string;
    tasks: IActivity[];
    currentActivityIndex: number;
    loading: boolean;
    onStartTasks: () => void; // enter ACTIVE mode, tasks pre-loaded
    onSwitchStudent: () => void;
    /** Override for testing; otherwise read from useAuthStore. */
    keyStage?: KeyStage;
}

const TYPE_EMOJI: Record<string, string> = {
    tracing: '✏️',
    letter_formation: '🔤',
    spelling: '📝',
    math: '🔢',
    maths: '🔢',
    free_write: '📖',
    sentence_build: '🔡',
    sentence_building: '🔡',
    comprehension: '📚',
    reading_comprehension: '📚',
    dictation: '🎤',
    copy_work: '📋',
    copy_writing: '📋',
    special_words: '⭐',
    common_exception_words: '⭐',
    science_observation: '🔬',
    history_source: '🏛️',
    geography_fieldnote: '🗺️',
    art_design_sketch: '🎨',
    computing_design: '💻',
    re_reflection: '🙏',
    interactive_story: '📖',
    story_reader: '📕',
    flashcard_review: '🃏',
    quiz: '❓',
    typed_composition: '⌨️',
    independent_writing: '✍️',
};

export function TaskQueue({
    studentName,
    tasks,
    currentActivityIndex,
    loading,
    onStartTasks,
    onSwitchStudent,
    keyStage,
}: TaskQueueProps) {
    const [previewCollapsed, setPreviewCollapsed] = useState(true);
    const [completedCollapsed, setCompletedCollapsed] = useState(true);

    const storeKeyStage = useAuthStore((s) => s.keyStage) as KeyStage | undefined;
    const effectiveKeyStage = keyStage ?? storeKeyStage;
    const tokens = useStageTokens(effectiveKeyStage);

    const isReady = useAuthStore((s) => s.isReady);
    const setReady = useAuthStore((s) => s.setReady);

    const pendingTasks = tasks.slice(currentActivityIndex);
    const completedTasks = tasks.slice(0, currentActivityIndex);

    const firstName = String(studentName || 'Student').split(' ')[0];
    const taskCountLabel = loading
        ? 'Loading your tasks...'
        : pendingTasks.length > 0
            ? `You have ${String(pendingTasks.length)} task${pendingTasks.length > 1 ? 's' : ''} to do`
            : 'No tasks yet — your teacher will send one soon!';
    // EYFS: read greeting + task count aloud on mount (voice-first rule).
    // KS1/KS2: tap the speaker icon to hear it.
    const greetingAudio = `Hi ${firstName}. ${taskCountLabel}`;

    const bounceAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        if (tasks.length > 0) return;
        const anim = Animated.loop(
            Animated.sequence([
                Animated.timing(bounceAnim, { toValue: 1, duration: 800, useNativeDriver: true }),
                Animated.timing(bounceAnim, { toValue: 0, duration: 800, useNativeDriver: true }),
            ])
        );
        anim.start();
        return () => anim.stop();
    }, [tasks.length]);

    const mascotScale = bounceAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 1.15],
    });

    const handleMarkReady = () => {
        if (isReady) return;
        setReady(true);
        StrokeSyncService.sendReady(true);
    };

    const renderTaskCard = (item: IActivity, index: number, isCompleted: boolean) => {
        const studentSafeTitle = getStudentSafeActivityTitle(item);
        return (
            <TouchableOpacity
                style={[styles.taskCard, isCompleted && styles.completedTaskCard]}
                key={item.id || `task-${index}`}
                onPress={isCompleted ? undefined : onStartTasks}
                activeOpacity={isCompleted ? 1 : 0.75}
                disabled={isCompleted}
                accessibilityLabel={studentSafeTitle}
                accessibilityRole="button"
                accessibilityHint={isCompleted ? 'This task is already finished' : 'Double tap to start this task'}
                accessibilityState={{ disabled: isCompleted }}
            >
                <View style={[styles.taskIndex, isCompleted && styles.completedTaskIndex]}>
                    <Text style={[styles.taskIndexText, isCompleted && styles.completedTaskIndexText]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {isCompleted ? '✓' : String(index + 1)}
                    </Text>
                </View>
                <View style={styles.taskEmoji}>
                    <Text style={styles.taskEmojiText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {String(TYPE_EMOJI[item.type] ?? '📝')}
                    </Text>
                </View>
                <View style={styles.taskInfo}>
                    <Text style={[styles.taskTitle, isCompleted && styles.completedTaskTitle]} numberOfLines={1} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {studentSafeTitle}
                    </Text>
                    <Text style={styles.taskType} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{String(item.type || '').replace(/_/g, ' ')}</Text>
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <ChildFriendlyBackground variant="light" scrollable={false}>
            <View style={styles.container}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={[styles.greeting, { fontSize: tokens.titleFontSize + 4 }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        Hi, {firstName}! 👋
                    </Text>
                    <View style={styles.headerSubRow}>
                        <Text style={[styles.subtitle, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            {taskCountLabel}
                        </Text>
                        <View style={styles.headerActions}>
                            <AudioPromptButton text={greetingAudio} autoPlay={tokens.autoPlayAudio} />
                            <TouchableOpacity
                                onPress={onSwitchStudent}
                                style={styles.switchBtn}
                                activeOpacity={0.75}
                                accessibilityLabel="Not me"
                                accessibilityRole="button"
                            >
                                <Text style={styles.switchBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Not me</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>

                {/* Body Content */}
                {loading ? (
                    <View style={styles.center}>
                        <View style={styles.loadingOrb}>
                            <Text style={styles.mascotEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🦊</Text>
                            <ActivityIndicator size="large" color="#1890FF" style={styles.spinner} />
                        </View>
                        <Text style={styles.loadingTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Checking your tasks...</Text>
                        <Text style={styles.loadingSubtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Hang tight while we get everything ready!</Text>
                    </View>
                ) : tasks.length === 0 ? (
                    /* Revamped Waiting for Teacher Screen (0 tasks state) */
                    <View style={styles.center}>
                        <View style={styles.waitCard}>
                            <Animated.View style={{ transform: [{ scale: mascotScale }] }}>
                                <Text style={styles.waitEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{isReady ? '🎉' : '🦊'}</Text>
                            </Animated.View>

                            <View style={styles.liveBadge}>
                                <View style={styles.liveDot} />
                                <Text style={styles.liveBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Connected to Class</Text>
                            </View>

                            <Text style={styles.waitTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {isReady ? "You're all set!" : `Welcome, ${firstName}!`}
                            </Text>

                            <Text style={styles.waitSubtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {isReady
                                    ? "We're waiting for the teacher to start the activity."
                                    : "Wait here and your teacher will send an activity soon. Tap the button below once you're ready!"}
                            </Text>

                            <AudioPromptButton
                                text={
                                    isReady
                                        ? "We're waiting for the teacher to start the activity."
                                        : `Welcome ${firstName}! Wait here and your teacher will send an activity soon. Tap I'm ready when you want to start.`
                                }
                                autoPlay={false}
                            />

                            <View style={{ height: 16 }} />

                            <FriendlyButton
                                variant={isReady ? 'primary' : 'secondary'}
                                size="large"
                                onPress={handleMarkReady}
                            >
                                {isReady ? "I'M READY! ✓" : "I'M READY!"}
                            </FriendlyButton>

                            {!isReady && (
                                <Text style={styles.waitStatusHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                    Tap when you're seated and ready!
                                </Text>
                            )}
                        </View>
                    </View>
                ) : (
                    /* Task Screen: Hero Card + Collapsible Preview */
                    <ScrollView contentContainerStyle={styles.list} showsVerticalScrollIndicator={false}>
                        {/* Ready to Start Hero Card */}
                        {pendingTasks.length > 0 && (
                            <View style={styles.heroCard}>
                                <View style={styles.heroBadge}>
                                    <Text style={styles.heroEmoji} allowFontScaling={true} maxFontSizeMultiplier={1.5}>🚀</Text>
                                </View>
                                <Text style={styles.heroTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Ready to begin!</Text>
                                <Text style={styles.heroSubtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                    {pendingTasks.length === 1
                                        ? 'You have 1 fun activity waiting for you.'
                                        : `You have ${pendingTasks.length} fun activities waiting for you.`}
                                </Text>
                                <TouchableOpacity
                                    testID="task-queue-hero-start-btn"
                                    style={styles.heroStartBtn}
                                    onPress={onStartTasks}
                                    activeOpacity={0.85}
                                    accessibilityLabel="Start tasks"
                                    accessibilityRole="button"
                                >
                                    <Text style={styles.heroStartBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>GO! ➔</Text>
                                </TouchableOpacity>
                            </View>
                        )}

                        {/* Collapsible Pending Tasks Section (Default: Collapsed) */}
                        {pendingTasks.length > 0 && (
                            <View style={styles.section}>
                                <TouchableOpacity
                                    testID="preview-tasks-toggle"
                                    style={styles.collapseHeader}
                                    onPress={() => setPreviewCollapsed(!previewCollapsed)}
                                    activeOpacity={0.7}
                                    accessibilityLabel={previewCollapsed ? 'Expand task preview' : 'Collapse task preview'}
                                    accessibilityRole="button"
                                >
                                    <View style={styles.collapseHeaderLeft}>
                                        <Text style={styles.sectionTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>PREVIEW TASKS ({pendingTasks.length})</Text>
                                        <View style={styles.countBadge}>
                                            <Text style={styles.countBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{pendingTasks.length}</Text>
                                        </View>
                                    </View>
                                    <Text style={styles.collapseIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                        {previewCollapsed ? '▼' : '▲'}
                                    </Text>
                                </TouchableOpacity>

                                {!previewCollapsed && (
                                    <View style={styles.taskListContainer}>
                                        <Text style={styles.previewHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                            Tasks are done in order. Tap any task to start!
                                        </Text>
                                        {pendingTasks.map((task, idx) =>
                                            renderTaskCard(task, currentActivityIndex + idx, false)
                                        )}
                                    </View>
                                )}
                            </View>
                        )}

                        {/* Completed Tasks Section */}
                        {completedTasks.length > 0 && (
                            <View style={styles.section}>
                                <TouchableOpacity
                                    style={styles.collapseHeader}
                                    onPress={() => setCompletedCollapsed(!completedCollapsed)}
                                    activeOpacity={0.7}
                                    accessibilityLabel={completedCollapsed ? 'Expand finished tasks' : 'Collapse finished tasks'}
                                    accessibilityRole="button"
                                >
                                    <View style={styles.collapseHeaderLeft}>
                                        <Text style={styles.sectionTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                            FINISHED ({String(completedTasks.length)})
                                        </Text>
                                    </View>
                                    <Text style={styles.collapseIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                        {completedCollapsed ? '▼' : '▲'}
                                    </Text>
                                </TouchableOpacity>

                                {!completedCollapsed && (
                                    <View style={styles.completedList}>
                                        {completedTasks.map((task, idx) => renderTaskCard(task, idx, true))}
                                    </View>
                                )}
                            </View>
                        )}
                    </ScrollView>
                )}
            </View>
        </ChildFriendlyBackground>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    header: {
        paddingTop: 16,
        paddingHorizontal: 20,
        paddingBottom: 16,
        borderBottomWidth: 1.5,
        borderBottomColor: '#BFDBFE',
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        gap: 8,
    },
    headerSubRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
    },
    headerActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    greeting: {
        fontSize: 26,
        fontWeight: '900',
        color: '#1E293B',
        letterSpacing: -0.5,
    },
    subtitle: {
        flex: 1,
        fontSize: 14,
        color: '#64748B',
        fontWeight: '600',
    },
    switchBtn: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        minHeight: 48,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
    },
    switchBtnText: {
        color: '#64748B',
        fontSize: 14,
        fontWeight: '700',
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
    },
    loadingOrb: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: '#FFF7E6',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 16,
        shadowColor: '#FAAD14',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 4,
    },
    mascotEmoji: {
        fontSize: 48,
    },
    spinner: {
        position: 'absolute',
    },
    loadingTitle: {
        color: '#1E293B',
        fontSize: 20,
        fontWeight: '800',
        marginTop: 8,
        textAlign: 'center',
    },
    loadingSubtitle: {
        color: '#64748B',
        fontSize: 15,
        fontWeight: '600',
        marginTop: 4,
        textAlign: 'center',
    },
    waitCard: {
        backgroundColor: '#FFFFFF',
        paddingVertical: 32,
        paddingHorizontal: 24,
        borderRadius: 28,
        alignItems: 'center',
        width: '100%',
        maxWidth: 480,
        shadowColor: '#1890FF',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 16,
        elevation: 6,
        borderWidth: 2,
        borderColor: '#BFDBFE',
    },
    waitEmoji: {
        fontSize: 64,
        marginBottom: 12,
    },
    liveBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F0FDF4',
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#86EFAC',
        gap: 6,
        marginBottom: 12,
    },
    liveDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: '#22C55E',
    },
    liveBadgeText: {
        color: '#15803D',
        fontSize: 12,
        fontWeight: '800',
    },
    waitTitle: {
        fontSize: 26,
        fontWeight: '900',
        color: '#1E293B',
        marginBottom: 8,
        textAlign: 'center',
    },
    waitSubtitle: {
        fontSize: 15,
        color: '#64748B',
        fontWeight: '600',
        textAlign: 'center',
        lineHeight: 22,
        marginBottom: 12,
        paddingHorizontal: 8,
    },
    waitStatusHint: {
        marginTop: 14,
        color: '#94A3B8',
        fontSize: 13,
        fontWeight: '600',
        textAlign: 'center',
    },
    list: {
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 40,
    },
    heroCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        paddingVertical: 28,
        paddingHorizontal: 24,
        alignItems: 'center',
        marginBottom: 24,
        borderWidth: 2,
        borderColor: '#BFDBFE',
        shadowColor: '#3B82F6',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 16,
        elevation: 5,
    },
    heroBadge: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: '#EFF6FF',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
        borderWidth: 2,
        borderColor: '#DBEAFE',
    },
    heroEmoji: {
        fontSize: 38,
    },
    heroTitle: {
        fontSize: 24,
        fontWeight: '900',
        color: '#1E293B',
        marginBottom: 6,
    },
    heroSubtitle: {
        fontSize: 15,
        fontWeight: '600',
        color: '#64748B',
        textAlign: 'center',
        marginBottom: 20,
    },
    heroStartBtn: {
        backgroundColor: '#22C55E',
        paddingVertical: 16,
        paddingHorizontal: 48,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: 320,
        shadowColor: '#22C55E',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
        elevation: 6,
    },
    heroStartBtnText: {
        color: '#FFFFFF',
        fontSize: 22,
        fontWeight: '900',
        letterSpacing: 0.5,
    },
    section: {
        marginBottom: 20,
    },
    collapseHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderRadius: 18,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.04,
        shadowRadius: 4,
        elevation: 1,
    },
    collapseHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: '900',
        color: '#475569',
        letterSpacing: 1.2,
    },
    countBadge: {
        backgroundColor: '#DCFCE7',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#86EFAC',
    },
    countBadgeText: {
        color: '#15803D',
        fontSize: 12,
        fontWeight: '800',
    },
    collapseIcon: {
        color: '#64748B',
        fontSize: 13,
        fontWeight: 'bold',
    },
    taskListContainer: {
        marginTop: 12,
    },
    previewHint: {
        fontSize: 13,
        color: '#64748B',
        fontWeight: '600',
        marginBottom: 10,
        marginLeft: 4,
    },
    taskCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        padding: 16,
        marginBottom: 10,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        gap: 14,
        shadowColor: '#1E293B',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.05,
        shadowRadius: 6,
        elevation: 2,
    },
    completedTaskCard: {
        opacity: 0.65,
        backgroundColor: '#F8FAFC',
        borderColor: '#E2E8F0',
    },
    taskIndex: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: '#DCFCE7',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#86EFAC',
    },
    completedTaskIndex: {
        backgroundColor: '#F1F5F9',
        borderColor: '#CBD5E1',
    },
    taskIndexText: {
        color: '#16A34A',
        fontWeight: '900',
        fontSize: 14,
    },
    completedTaskIndexText: {
        color: '#64748B',
    },
    taskEmoji: {
        width: 46,
        height: 46,
        borderRadius: 14,
        backgroundColor: '#F1F5F9',
        alignItems: 'center',
        justifyContent: 'center',
    },
    taskEmojiText: {
        fontSize: 24,
    },
    taskInfo: {
        flex: 1,
    },
    taskTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#1E293B',
    },
    completedTaskTitle: {
        color: '#94A3B8',
        textDecorationLine: 'line-through',
    },
    taskType: {
        fontSize: 13,
        color: '#64748B',
        fontWeight: '600',
        marginTop: 2,
        textTransform: 'capitalize',
    },
    completedList: {
        marginTop: 12,
    },
});
